import { Component, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BookingRequestResponse, SelectedSlot } from '../../models/public-calendar.models';
import { PublicI18nService } from '../../services/public-i18n.service';

@Component({
  selector: 'pc-request-success',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="rounded-lg border border-success/30 bg-success/10 p-6 text-center">
      <!-- Success icon -->
      <div class="mx-auto w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h2 class="text-2xl font-bold text-success mb-2">{{ i18n.t('success.title') }}</h2>
      <p class="text-success mb-6">
        {{ i18n.t('success.message') }}
      </p>

      <!-- Booking details -->
      @if (slot()) {
        <div class="bg-card rounded-lg p-4 mb-6 text-left">
          <h3 class="font-medium text-foreground mb-2">{{ i18n.t('success.requestedSlot') }}</h3>
          <p class="text-muted-foreground">
            {{ formatDate(slot()!.date) }}<br />
            {{ i18n.formatTime(slot()!.startTime) }} - {{ i18n.formatTime(slot()!.endTime) }} {{ i18n.t('success.clock') }}
          </p>
        </div>
      }

      <!-- What happens next -->
      <div class="bg-card rounded-lg p-4 text-left mb-6">
        <h3 class="font-medium text-foreground mb-3">{{ i18n.t('success.nextSteps') }}</h3>
        <ol class="space-y-2 text-sm text-muted-foreground">
          <li class="flex gap-2">
            <span class="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
            <span>{{ i18n.t('success.step1') }}</span>
          </li>
          <li class="flex gap-2">
            <span class="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
            <span>{{ i18n.t('success.step2') }}</span>
          </li>
          <li class="flex gap-2">
            <span class="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">3</span>
            <span>{{ i18n.t('success.step3') }}</span>
          </li>
        </ol>
      </div>

      <!-- Tracking info -->
      @if (response()) {
        <p class="text-sm text-muted-foreground mb-4">
          {{ i18n.t('success.requestId') }}: <code class="bg-muted px-2 py-1 rounded">{{ response()!.requestId.slice(0, 8) }}</code>
        </p>
      }

      <!-- New request button -->
      <button
        type="button"
        class="btn-primary btn-md"
        (click)="newRequest.emit()"
      >
        {{ i18n.t('success.newRequest') }}
      </button>
    </div>
  `,
})
export class RequestSuccessComponent {
  readonly i18n = inject(PublicI18nService);

  response = input<BookingRequestResponse | null>(null);
  slot = input<SelectedSlot | null>(null);

  newRequest = output<void>();

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(this.i18n.getDateLocale(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
}
